import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabaseClient';
import { useAuth } from './hooks/useAuth';

import './App.css';
import iconBlack from './images/icon_black.png';
import iconBlue from './images/icon_blue.png';
import iconGrey from './images/icon_grey.png';
import iconRed from './images/icon_red.png';
import researchIcon from './assets/research.png';
import Events from './Events';
import Workouts from './Workouts';
import WorkoutsNew from './WorkoutsNew';
import Tunes from './Tunes';
import AboutUs from './AboutUs';
import AboutMuayThai from './AboutMuayThai';
import AboutMusic from './AboutMusic';
import MeldingPage from './MeldingPage';
import BringerofWorlds from './BringerofWorlds';
import KrabbyPattyFormula from './KrabbyPattyFormula';
import Research from './Research';
import BottomBar from './components/BottomBar';

const panel1 = {
  name: "Events",
  imageLocation: iconRed,
  link: "/events"
};

const panel2 = { ...panel1, name: "Workouts", imageLocation: iconGrey, link:"/workouts" };
const panel3 = { ...panel1, name: "Tunes", imageLocation: iconBlue, link: "/tunes" };
const panel4 = { ...panel1, name: "About Us", imageLocation: iconBlack, link: "/aboutus" };

let panels = [panel1, panel2, panel3, panel4];

function Home() {
  const { user, dbUser } = useAuth();
  
  // Admin authority comes only from the database boolean.
  const isAdmin = user && dbUser?.is_admin === true;
  
  console.log('Home component rendering...');
  console.log('Panels data:', panels);
  console.log('Panel images:', panels.map(p => p.imageLocation));
  
  return (
    <div className="home-container">
      <div className="home-nav-container">
        {/* Research icon row - centered above, only for admins */}
        {isAdmin && (
          <div className="home-nav-research-row">
            <div className="home-nav-panel">
              <div className="home-nav-item home-nav-research-item">
                <Link className="home-nav-link" to="/research">
                  <img
                    className="home-nav-image home-nav-research-image"
                    src={researchIcon}
                    alt="Research"
                    onLoad={() => console.log('Research image loaded')}
                    onError={(e) => console.error('Research image failed to load:', e)}
                  />
                  <div className="home-nav-label">
                    <div className="home-nav-label-text">RESEARCH</div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
        
        {/* Main navigation icons row */}
        <div className="home-nav-grid">
          {panels.map((panel, index) => {
            console.log(`Rendering panel ${index}:`, panel.name, panel.imageLocation);
            return (
              <div key={index} className="home-nav-panel">
                <div className="home-nav-item">
                  <Link className="home-nav-link" to={panel.link}>
                    <img
                      className="home-nav-image"
                      src={panel.imageLocation}
                      alt={'Photo of ' + panel.name}
                      onLoad={() => console.log(`Image loaded: ${panel.name}`)}
                      onError={(e) => console.error(`Image failed to load: ${panel.name}`, e)}
                    />
                    <div className="home-nav-label">
                      <div className="home-nav-label-text">{panel.name}</div>
                    </div>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function App() {
  const { user, signInWithGoogle, signOut, loading: authLoading } = useAuth();
  const location = useLocation();
  
  // Hide top bar on /workouts routes
  const hideTopBar = location.pathname.startsWith('/workouts');
  
  console.log('App component rendering...');
  console.log('Current URL:', window.location.href);
  console.log('Current pathname:', window.location.pathname);
  
  return (
    <div className="App">
      {/* Top bar with sign-in/sign-out - hidden on /workouts routes */}
      {!hideTopBar && (
        <div className="top-bar">
          <div className="top-bar-content">
            <h1 className="top-bar-title">
              Welcome to Muay Thai Beats!{' '}
              <span className="home-instruction-fine-pointer">Hover over logos.</span>
              <span className="home-instruction-touch">Tap a logo to enter.</span>
            </h1>
            <div className="top-bar-auth">
              {!authLoading && (
                <>
                  {user ? (
                    <div className="top-bar-user-info">
                      <span className="user-name">
                        {user.user_metadata?.full_name || user.email}
                      </span>
                      <button
                        className="button is-light"
                        onClick={signOut}
                      >
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <button
                      className="button is-primary"
                      onClick={signInWithGoogle}
                    >
                      Sign in with Google
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <OAuthCallbackHandler />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/events" element={<EventsWrapper />} />
        <Route path="/workouts" element={<WorkoutsWrapper />} />
        <Route path="/workouts/new" element={<NewWorkoutWrapper />} />
        <Route path="/tunes" element={<TunesWrapper />} />
        <Route path="/aboutus" element={<AboutUsWrapper />} />
        <Route path="/aboutus/about-muaythai" element={<AboutMuayThaiWrapper />} />
        <Route path="/aboutus/about-music" element={<AboutMusicWrapper />} />
        <Route path="/aboutus/melding-muaythai-music" element={<MeldingPageWrapper />} />
        <Route path="/bringerofworlds" element={<BringerofWorldsWrapper />} />
        <Route path="/krabbypattyformula" element={<KrabbyPattyFormulaWrapper />} />
        <Route path="/research" element={<ResearchWrapper />} />
      </Routes>
      <BottomBar />
    </div>
  );
}

// OAuth callback handler component
function OAuthCallbackHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [hasProcessedHash, setHasProcessedHash] = useState(false);

  // Handle returnTo navigation after sign-in (fallback if hash processing doesn't catch it)
  useEffect(() => {
    if (!user) return; // Only run when user is signed in
    
    // Only check returnTo if we're not currently processing a hash
    // This prevents race conditions with the hash processing useEffect
    const hash = window.location.hash;
    const hasOAuthParams = hash && (
      hash.includes('access_token') ||
      hash.includes('refresh_token') ||
      hash.includes('error')
    );
    if (hasOAuthParams) return; // Let hash processing handle it

    const returnTo = sessionStorage.getItem('returnTo');
    if (returnTo) {
      console.log('[OAuthCallbackHandler] Found returnTo (fallback), navigating to:', returnTo);
      sessionStorage.removeItem('returnTo');
      // Only navigate if we're not already on that path
      const currentPath = location.pathname + location.search + (location.hash || '');
      if (currentPath !== returnTo) {
        navigate(returnTo, { replace: true });
      }
    }
  }, [user, navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    // Only process hash once
    if (hasProcessedHash) return;

    // Check if URL hash contains OAuth callback params (access_token, refresh_token, etc.)
    const hash = window.location.hash;
    const hasOAuthParams = hash && (
      hash.includes('access_token') ||
      hash.includes('refresh_token') ||
      hash.includes('error')
    );

    if (hasOAuthParams) {
      console.log('[OAuthCallbackHandler] Detected OAuth callback hash, processing...');
      setHasProcessedHash(true);

      // Supabase automatically processes the hash when getSession() is called
      // The hash contains tokens that Supabase will extract and store
      // We need to wait for the session to be processed from the hash
      supabase.auth.getSession()
        .then(({ data: { session }, error }) => {
          if (error) {
            console.error('[OAuthCallbackHandler] Error getting session from hash:', error);
          } else if (session) {
            console.log('[OAuthCallbackHandler] Session processed from hash, user:', session.user?.email);
          } else {
            console.log('[OAuthCallbackHandler] No session found after processing hash');
          }

          // Clear the hash from URL AFTER session is processed
          // This must happen after Supabase processes the tokens from the hash
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search
          );

          // Check for returnTo and navigate there
          const returnTo = sessionStorage.getItem('returnTo');
          if (returnTo) {
            console.log('[OAuthCallbackHandler] Found returnTo, navigating to:', returnTo);
            sessionStorage.removeItem('returnTo');
            // Only navigate if we're not already on that path
            const currentPath = location.pathname + location.search;
            if (currentPath !== returnTo.split('#')[0]) {
              navigate(returnTo, { replace: true });
            }
          }
          // If no returnTo, user stays on current page
        })
        .catch((error) => {
          console.error('[OAuthCallbackHandler] Error processing OAuth callback:', error);
          // Still clear the hash even on error
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname + window.location.search
          );
        });
    }
  }, [navigate, location.pathname, location.search, hasProcessedHash]);

  return null; // This component doesn't render anything
}
function AboutMuayThaiWrapper() {
  return (
    <div className="content-container">
      <AboutMuayThai />
    </div>
  );
}
function AboutMusicWrapper() {
  return (
    <div className="content-container">
      <AboutMusic />
    </div>
  );
}
function MeldingPageWrapper() {
  return (
    <div className="content-container">
      <MeldingPage />
    </div>
  );
}

function EventsWrapper() {
  return (
    <div className="content-container">
      <Events />
    </div>
  );
}

function WorkoutsWrapper() {
  return (
    <div className="content-container">
      <Workouts />
    </div>
  );
}

function TunesWrapper() {
  return (
    <div className="content-container">
      <Tunes />
    </div>
  );
}

function AboutUsWrapper() {
  return (
    <div className="content-container">
      <AboutUs />
    </div>
  );
}

function BringerofWorldsWrapper() {
  return (
    <div className="content-container">
      <BringerofWorlds />
    </div>
  );
}

function KrabbyPattyFormulaWrapper() {
  return (
    <div className="content-container">
      <KrabbyPattyFormula />
    </div>
  );
}

function NewWorkoutWrapper() {
  return (
    <div className="content-container">
      <WorkoutsNew />
    </div>
  );
}

function ResearchWrapper() {
  return (
    <div className="content-container">
      <Research />
    </div>
  );
}

export default App;
